/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useSelector } from 'react-redux';
import { getPageNumbersWithEmailQuestion } from '../../Helpers/components';
import makeBrandMatchMessageStyling from './makeBrandMatchMessageStyling';

const BrandMatchMessage = () => {
  const theme = useTheme();
  const styling = makeBrandMatchMessageStyling({ theme });

  const currentPage = useSelector((store) => store.navigation.currentPage);
  const components = useSelector((store) => store.components);
  const matchingSupplierCount = useSelector(
    (store) => store.supplierMatches.matchingSupplierCount
  );
  const matchingSupplierCountLoading = useSelector(
    (store) => store.supplierMatches.matchingSupplierCountLoading
  );
  const showBrandMatchCount = useSelector(
    (store) => store.features.showBrandMatchCount
  );

  if (!showBrandMatchCount) {
    return null;
  }

  const emailPageNumbers = getPageNumbersWithEmailQuestion(components);
  const firstEmailPage =
    emailPageNumbers.length > 0 ? Math.min(...emailPageNumbers) : null;

  if (firstEmailPage !== null && currentPage >= firstEmailPage) {
    return null;
  }

  const shouldShowIntro = matchingSupplierCount === null;
  const showSpinner = matchingSupplierCountLoading;

  let messageContent;
  if (showSpinner) {
    messageContent = (
      <Typography css={styling.text}>
        Finding the best providers for you...
      </Typography>
    );
  } else if (shouldShowIntro) {
    messageContent = (
      <Typography css={styling.text}>
        Start answering questions to find your best match
      </Typography>
    );
  } else {
    messageContent = (
      <Typography css={styling.text}>
        <Box component="span" css={styling.count} data-testid="brandMatchCount">
          {matchingSupplierCount}
        </Box>{' '}
        providers being analyzed. Keep answering to find the best matches!
      </Typography>
    );
  }

  return (
    <Box css={styling.container} data-testid="brandMatchMessage">
      <Box css={styling.card}>
        <Box css={styling.contentWrapper}>
          <Box css={styling.iconWrapper}>
            {showSpinner ? (
              <CircularProgress
                size={20}
                thickness={4}
                css={styling.spinner}
                data-testid="brandMatchSpinner"
              />
            ) : (
              <Box css={styling.icon}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 11L12 14L22 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            )}
          </Box>

          <Box css={styling.textWrapper}>{messageContent}</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BrandMatchMessage;
